import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, CircleDashed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { InlineExecutiveAi } from "@/components/renata/InlineExecutiveAi";
import type { AIHealthEntry } from "@/lib/ai/types";

export const Route = createFileRoute("/admin/ops/ai/dashboard")({
  component: AdminAIDashboardPage,
  head: () => ({ meta: [{ title: "Panel IA — Admin" }] }),
});

interface UsageRow {
  total_queries: number;
  total_cost_microcents: number;
  total_tokens: number;
}

interface UsagePayload {
  days: number;
  byTenant: Array<UsageRow & { tenant_id: string; provider: string; errors?: number }>;
  byProvider: Array<UsageRow & { provider: string }>;
  byModel: Array<UsageRow & { model: string; provider: string }>;
  byDay: Array<UsageRow & { day: string }>;
  byMonth: Array<UsageRow & { month: string }>;
}

function StatusIcon({ status }: { status: string }) {
  if (status === "healthy") return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (status === "degraded") return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
  if (status === "down") return <XCircle className="h-4 w-4 text-red-500" />;
  return <CircleDashed className="h-4 w-4 text-muted-foreground" />;
}

function formatCost(microcents: number): string {
  return `$${(Number(microcents) / 100_000_000).toFixed(4)}`;
}

function formatNum(n: number): string {
  return Number(n).toLocaleString("es-AR");
}

function TokenLimitRow({ tenantId }: { tenantId: string }) {
  const [limit, setLimit] = useState<string>("");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const monthlyTokenLimit = limit.trim() === "" || limit.trim() === "0" ? null : Number(limit);
      await fetch("/api/admin/ai/usage", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId, monthlyTokenLimit }),
      });
      toast.success("Límite actualizado");
    } catch {
      toast.error("Error al guardar límite");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        className="h-7 w-32 text-xs"
        placeholder="Sin límite"
        value={limit}
        onChange={(e) => setLimit(e.target.value)}
        type="number"
        min={0}
      />
      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={saving} onClick={save}>
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Guardar"}
      </Button>
    </div>
  );
}

function AdminAIDashboardPage() {
  const [health, setHealth] = useState<AIHealthEntry[]>([]);
  const [usage, setUsage] = useState<UsagePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLimits, setShowLimits] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [healthRes, usageRes] = await Promise.all([
        fetch("/api/admin/ai/health", { credentials: "include" }),
        fetch("/api/admin/ai/usage?days=30", { credentials: "include" }),
      ]);
      const healthData = await healthRes.json() as { health: AIHealthEntry[] };
      const usageData = await usageRes.json() as UsagePayload;
      setHealth(healthData.health ?? []);
      setUsage(usageData);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const totals = usage?.byProvider.reduce(
    (acc, r) => ({
      queries: acc.queries + Number(r.total_queries),
      tokens: acc.tokens + Number(r.total_tokens),
      cost: acc.cost + Number(r.total_cost_microcents),
    }),
    { queries: 0, tokens: 0, cost: 0 },
  ) ?? { queries: 0, tokens: 0, cost: 0 };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">AI Dashboard</h1>
          <p className="text-sm text-muted-foreground">Estado de proveedores y consumo global (últimos 30 días).</p>
          <div className="mt-2"><InlineExecutiveAi /></div>
        </div>
        <Button size="sm" variant="outline" className="gap-2" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border bg-card p-4">
              <p className="text-xs text-muted-foreground">Consultas</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{formatNum(totals.queries)}</p>
            </div>
            <div className="rounded-xl border bg-card p-4">
              <p className="text-xs text-muted-foreground">Consultas</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{formatNum(totals.tokens)}</p>
            </div>
            <div className="rounded-xl border bg-card p-4">
              <p className="text-xs text-muted-foreground">Costo estimado</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{formatCost(totals.cost)}</p>
            </div>
          </div>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold">Estado de proveedores</h2>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Proveedor</th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Modelo</th>
                    <th className="px-4 py-2 text-center font-medium text-muted-foreground">Estado</th>
                    <th className="px-4 py-2 text-right font-medium text-muted-foreground">Latencia</th>
                  </tr>
                </thead>
                <tbody>
                  {health.map((h) => (
                    <tr key={h.provider} className="border-b last:border-0">
                      <td className="px-4 py-2 font-medium">{h.provider}</td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{h.model ?? "—"}</td>
                      <td className="px-4 py-2">
                        <div className="flex justify-center"><StatusIcon status={h.status} /></div>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {h.latencyMs == null ? "—" : h.latencyMs < 1000 ? `${h.latencyMs}ms` : `${(h.latencyMs / 1000).toFixed(1)}s`}
                      </td>
                    </tr>
                  ))}
                  {health.length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-sm text-muted-foreground">Sin datos de health.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">Uso por tenant / provider</h2>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowLimits((v) => !v)}>
                {showLimits ? "Ocultar límites" : "Gestionar límites de tokens"}
              </Button>
            </div>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Tenant</th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Provider</th>
                    <th className="px-4 py-2 text-right font-medium text-muted-foreground">Queries</th>
                    <th className="px-4 py-2 text-right font-medium text-muted-foreground">Consultas</th>
                    <th className="px-4 py-2 text-right font-medium text-muted-foreground">Costo</th>
                    {showLimits && <th className="px-4 py-2 text-left font-medium text-muted-foreground">Límite/mes</th>}
                  </tr>
                </thead>
                <tbody>
                  {(usage?.byTenant ?? []).slice(0, 40).map((r, i) => (
                    <tr key={`${r.tenant_id}-${r.provider}-${i}`} className="border-b last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">{r.tenant_id}</td>
                      <td className="px-4 py-2">{r.provider}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatNum(r.total_queries)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatNum(r.total_tokens)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatCost(r.total_cost_microcents)}</td>
                      {showLimits && (
                        <td className="px-4 py-2"><TokenLimitRow tenantId={r.tenant_id} /></td>
                      )}
                    </tr>
                  ))}
                  {(usage?.byTenant ?? []).length === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-sm text-muted-foreground">Sin uso registrado.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div className="grid gap-6 lg:grid-cols-2">
            <section className="space-y-2">
              <h2 className="text-sm font-semibold">Por modelo</h2>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">Modelo</th>
                      <th className="px-4 py-2 text-right font-medium text-muted-foreground">Queries</th>
                      <th className="px-4 py-2 text-right font-medium text-muted-foreground">Costo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(usage?.byModel ?? []).slice(0, 20).map((r, i) => (
                      <tr key={`${r.model}-${i}`} className="border-b last:border-0">
                        <td className="px-4 py-2">
                          <div className="font-mono text-xs">{r.model}</div>
                          <div className="text-xs text-muted-foreground">{r.provider}</div>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatNum(r.total_queries)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatCost(r.total_cost_microcents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="space-y-2">
              <h2 className="text-sm font-semibold">Diario / mensual</h2>
              <div className="overflow-x-auto rounded-lg border mb-3">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">Día</th>
                      <th className="px-4 py-2 text-right font-medium text-muted-foreground">Queries</th>
                      <th className="px-4 py-2 text-right font-medium text-muted-foreground">Costo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(usage?.byDay ?? []).slice(0, 14).map((r) => (
                      <tr key={String(r.day)} className="border-b last:border-0">
                        <td className="px-4 py-2 text-xs">{String(r.day).slice(0, 10)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatNum(r.total_queries)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatCost(r.total_cost_microcents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">Mes</th>
                      <th className="px-4 py-2 text-right font-medium text-muted-foreground">Queries</th>
                      <th className="px-4 py-2 text-right font-medium text-muted-foreground">Costo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(usage?.byMonth ?? []).map((r) => (
                      <tr key={r.month} className="border-b last:border-0">
                        <td className="px-4 py-2 text-xs">{r.month}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatNum(r.total_queries)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatCost(r.total_cost_microcents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
